import { html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement } from '../base-element';
import { PopoverBaseWC } from '../popover/src/popover-base.wc';
import styles from './popover-tooltip.scss?inline';

@customElement('ps-popover-tooltip')
export class PopoverTooltipWC extends PopoverBaseWC {
  static styles = unsafeCSS(styles);

  /**
   * Popover size
   */
  @property({ reflect: true }) size: 'none' | 'xsmall' | 'small' | 'large' =
    'large';

  constructor() {
    super();
    this.distance = 12;
    this.skidding = 8;
    this.hasArrow = true;

    this.triggerAction = 'click-and-hover';
    this.closeOnClickOutside = false;
    this.disableFocusTrap = true;
  }

  /**
   * True when the tooltip has non-whitespace slotted content to display.
   * Guards against showing an empty bubble (PSTUDIO-3335): the per-cell error
   * tooltip in Pro Studio is mounted unconditionally (PSTUDIO-2321) and its
   * `click-and-hover` trigger calls `show()` on mouseover regardless of whether
   * there is an error message, so an empty cell would otherwise pop a blank
   * bubble.
   *
   * Read at evaluation time (no `slotchange` listener): the `--shown` gate is
   * only re-checked when the reactive `active` property changes, so consumers
   * that mutate slotted text while the tooltip is already shown must toggle
   * `active` to re-evaluate. Today's sole consumer derives both from the same
   * `errorMessage`, so they always change together.
   */
  private get hasContent(): boolean {
    return (this.textContent ?? '').trim().length > 0;
  }

  /**
   * Never open an empty tooltip. Every trigger path (hover, click, focus,
   * keyboard, defaultActive) funnels through `show()`, so a single guard here
   * covers them all.
   */
  show(): void {
    if (!this.hasContent) return;
    super.show();
  }

  render() {
    return html`
      <div
        id="popover-container"
        class=${classMap({
          'c-popover-tooltip': true,
          'c-popover-tooltip--shown': this.active && this.hasContent,
        })}
        style="transform: ${this.transform}"
      >
        <slot class="c-popover-tooltip__body"></slot>

        <div class="c-popover-tooltip__arrow" id="popover-arrow"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-popover-tooltip': PopoverTooltipWC;
  }
  enum PSElementTagNameMap {
    'ps-popover-tooltip' = 'ps-ppopover-tooltip',
  }
}
